import { isLegacySeatLicenseType, resolveSeatLicenseType } from './portalOrganization.js'
import {
  formatBillingDocumentDate,
} from './portalBillingDocuments.js'
import {
  resolvePurchaseHistoryPaidStatusBadge,
  INITIAL_SEAT_POOL_LABEL,
  isInitialSeatPoolBatch,
} from './portalPurchaseHistoryDisplay.js'

/** Legacy seats expire on this calendar date (local), per product policy. */
export const LEGACY_SEAT_EXPIRY = { year: 2028, month: 7, day: 1 }

export function legacySeatExpiryDate() {
  return new Date(LEGACY_SEAT_EXPIRY.year, LEGACY_SEAT_EXPIRY.month, LEGACY_SEAT_EXPIRY.day)
}

/**
 * Parse YYYY-MM-DD (or ISO prefix) as local midnight — avoids UTC off-by-one in US zones.
 * @param {string | Date | null | undefined} value
 */
export function parseSeatPurchaseDate(value) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const s = String(value ?? '').slice(0, 10)
  const parts = s.split('-').map((n) => Number(n))
  if (parts.length === 3 && parts.every((n) => Number.isFinite(n))) {
    return new Date(parts[0], parts[1] - 1, parts[2])
  }
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? new Date() : d
}

/**
 * License type for a batch — uses the batch record only, not the org's current plan.
 * @param {{ type?: string, licenseType?: string }} batch
 */
export function resolveBatchSeatLicenseType(batch) {
  const raw = batch?.type ?? batch?.licenseType
  if (!raw) return null
  return resolveSeatLicenseType(raw)
}

/**
 * Client fallback when batch.expiresAt is omitted (matches billingLicensing categories).
 * @param {{ purchaseDate?: string, type?: string, licenseType?: string }} batch
 * @returns {Date | null} null = never expires (Premium)
 */
export function computeSeatBatchExpiryDate(batch) {
  const purchase = parseSeatPurchaseDate(batch?.purchaseDate)
  const purchaseExpiry = new Date(
    purchase.getFullYear() + 1,
    purchase.getMonth(),
    purchase.getDate()
  )
  const batchType = resolveBatchSeatLicenseType(batch)

  if (batchType === 'Premium' || batchType === 'Supp. Premium') return null
  if (isLegacySeatLicenseType(batchType)) return legacySeatExpiryDate()
  if (batchType === 'Basic' || batchType === 'Supp. Basic') return purchaseExpiry

  return purchaseExpiry
}

/**
 * Effective expiry for display — prefers server batch.expiresAt when set.
 * @param {{ expiresAt?: string | Date | null, purchaseDate?: string, type?: string, licenseType?: string, status?: string }} batch
 * @returns {Date | null}
 */
export function resolveBatchExpiryDate(batch) {
  if (!batch || batch.status === 'expired') return null
  const raw = batch.expiresAt
  if (raw != null && raw !== '') {
    const d = raw instanceof Date ? raw : new Date(raw)
    if (!Number.isNaN(d.getTime())) return d
  }
  return computeSeatBatchExpiryDate(batch)
}

/**
 * Available unassigned seats in one batch (billingLicensing.getBatchAvailable).
 * @param {{ quantity?: number, used?: number, consumed?: number, expiredQuantity?: number, status?: string, expiresAt?: string | Date | null }} batch
 * @param {Date} [now]
 */
export function getBatchAvailable(batch, now = new Date()) {
  if (!batch || batch.status === 'expired') return 0
  if (batch.expiresAt && new Date(batch.expiresAt) <= now) return 0
  const qty = Number(batch.quantity) || 0
  const used = Number(batch.used) || Number(batch.consumed) || 0
  const expiredQty = Number(batch.expiredQuantity) || 0
  return Math.max(0, qty - used - expiredQty)
}

/** @param {Record<string, unknown>} batch */
export function getBatchSeatBreakdown(batch, now = new Date()) {
  const quantity = Number(batch?.quantity) || 0
  const assigned = Number(batch?.used ?? batch?.consumed) || 0
  const expiredQty = Number(batch?.expiredQuantity) || 0
  const available = getBatchAvailable(batch, now)
  return { quantity, assigned, expiredQty, available }
}

/**
 * @param {Date | null} expiryDate
 */
export function computeSeatDaysLeft(expiryDate, now = new Date()) {
  if (!expiryDate) return null
  return Math.max(0, Math.ceil((expiryDate - now) / 86400000))
}

/**
 * @param {Date | null} expiryDate
 */
export function formatSeatExpiryDate(expiryDate) {
  if (!expiryDate) return 'Never'
  return expiryDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * @param {number | null} daysLeft
 */
export function seatExpiryStatus(daysLeft) {
  if (daysLeft === null) return 'No Expiration'
  if (daysLeft <= 0) return 'Expired'
  if (daysLeft <= 30) return 'Expiring Soon'
  return 'Active'
}

/**
 * Insight rows from organization.seatPurchaseBatches only (batches with available seats).
 * @param {Array<Record<string, unknown>>} batches
 */
export function buildSeatInsightEntriesFromBatches(batches) {
  const now = new Date()
  return (Array.isArray(batches) ? batches : [])
    .map((b) => {
      const remaining = getBatchAvailable(b, now)
      if (remaining <= 0) return null
      const expDate = resolveBatchExpiryDate(b)
      const daysLeft = computeSeatDaysLeft(expDate, now)
      return {
        daysLeft,
        remaining,
        status: seatExpiryStatus(daysLeft),
        expiryFormatted: formatSeatExpiryDate(expDate),
        expDate,
      }
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.expDate == null && b.expDate == null) return 0
      if (a.expDate == null) return 1
      if (b.expDate == null) return -1
      return a.expDate - b.expDate
    })
}

/** Soonest future expiration among entries that still have seats remaining. */
export function pickSoonestSeatExpiry(entries) {
  const candidates = (entries || []).filter(
    (b) =>
      b.expDate != null &&
      b.daysLeft != null &&
      b.daysLeft > 0 &&
      (b.remaining ?? 0) > 0
  )
  if (!candidates.length) return null
  return candidates.reduce(
    (earliest, b) => (b.expDate < earliest.expDate ? b : earliest),
    candidates[0]
  )
}

/**
 * Map billing document id / invoice # → batch for purchase-history table seat columns.
 * @param {Record<string, unknown>} entry
 * @param {Array<Record<string, unknown>>} batches
 */
function purchaseHistoryEntryIncludesSeatPurchase(entry, batches = []) {
  if (Math.abs(Number(entry?.amount) || 0) > 0.01) return true
  if (Number(entry?.seats) > 0) return true
  const lines = Array.isArray(entry?.lineItems)
    ? entry.lineItems
    : Array.isArray(entry?.billingDocument?.lineItems)
      ? entry.billingDocument.lineItems
      : []
  if (lines.some((line) => String(line?.key || '').trim() === 'seats' && (Number(line.quantity) || 0) > 0)) {
    return true
  }
  const docId = String(entry?.documentId ?? entry?.id ?? '')
  if (!docId) return false
  return (Array.isArray(batches) ? batches : []).some((batch) =>
    String(batch?.sourceType || '').toLowerCase() === 'purchase'
    && batch?.billingDocumentId
    && String(batch.billingDocumentId) === docId
  )
}

export function findSeatBatchForPurchaseHistoryEntry(entry, batches) {
  const list = Array.isArray(batches) ? batches : []
  const docId = String(entry.documentId ?? entry.id ?? entry.billingDocumentId ?? '')
  const entryNum = normalizeInvoiceNumber(entry.number)
  const purchasedSeats = purchaseHistoryEntryIncludesSeatPurchase(entry, list)

  if (docId) {
    const byDocument = list.find(
      (batch) => batch.billingDocumentId && String(batch.billingDocumentId) === docId
    )
    if (byDocument) return byDocument
  }

  for (const batch of list) {
    if (purchasedSeats && isInitialSeatPoolBatch(batch)) continue
    const batchNum = normalizeInvoiceNumber(batch.invoiceNum)
    if (batchNum && entryNum && batchNum === entryNum) return batch
    if (docId && batch.id && String(batch.id) === docId) return batch
  }
  return null
}

function normalizeInvoiceNumber(value) {
  return String(value ?? '')
    .replace(/^#/, '')
    .trim()
    .toLowerCase()
}

/** @param {string | undefined | null} sourceType */
export function batchSourceTypeLabel(sourceType) {
  const key = String(sourceType || '').toLowerCase()
  if (key === 'legacy_backfill' || key === 'migration' || key === 'legacy_migration') {
    return INITIAL_SEAT_POOL_LABEL
  }
  if (key === 'conversion') return 'Conversion'
  if (key === 'purchase') return 'Seat purchase'
  return 'Seat batch'
}

/**
 * Purchase-history row for a seat batch with no billing document (e.g. legacy_backfill).
 * @param {Record<string, unknown>} batch
 * @param {number} index
 */
export function mapSeatBatchToPurchaseHistoryEntry(batch, index = 0) {
  const purchaseDate =
    batch.purchaseDate ||
    (batch.createdAt ? String(batch.createdAt).slice(0, 10) : null) ||
    new Date().toISOString().slice(0, 10)
  const sourceType = batch.sourceType ?? null
  const quantity = Number(batch.quantity) || 0
  const number = isInitialSeatPoolBatch(batch)
    ? INITIAL_SEAT_POOL_LABEL
    : batch.invoiceNum
      ? String(batch.invoiceNum).startsWith('#')
        ? batch.invoiceNum
        : `#${batch.invoiceNum}`
      : `#BATCH-${String(batch.id ?? index).slice(0, 8)}`

  return {
    id: batch.id ?? `batch-row-${index}`,
    number,
    date: formatBillingDocumentDate(purchaseDate),
    amount: 0,
    type: batchSourceTypeLabel(sourceType),
    documentType: 'seat_batch',
    purchaseDate: String(purchaseDate).slice(0, 10),
    documentId: batch.billingDocumentId ?? null,
    status: batch.status === 'expired' ? 'expired' : 'paid',
    statusLabel: batch.status === 'expired' ? 'Expired' : 'Active',
    license: batch.type ?? batch.licenseType,
    seats: quantity,
    hasConversionLine: batch.lineItemKey === 'conversion',
    sourceType,
    isSeatBatchRow: true,
    batchId: batch.id,
    billingDocumentId: batch.billingDocumentId ?? null,
    seatBatch: batch,
    fulfillmentStatus: 'fulfilled',
  }
}

/**
 * Append seat batches that are not already represented by a billing document row.
 * @param {Array<Record<string, unknown>>} billingEntries
 * @param {Array<Record<string, unknown>>} batches
 */
export function mergePurchaseHistoryWithSeatBatches(billingEntries, batches) {
  const base = Array.isArray(billingEntries) ? billingEntries : []
  const batchList = Array.isArray(batches) ? batches : []

  /** @type {Set<string>} */
  const hiddenDocIds = new Set()
  /** @type {Set<string>} */
  const hiddenInvoiceNumbers = new Set()
  for (const batch of batchList) {
    if (!isInitialSeatPoolBatch(batch)) continue
    if (batch.billingDocumentId) {
      hiddenDocIds.add(String(batch.billingDocumentId))
    }
    const batchNum = normalizeInvoiceNumber(batch.invoiceNum)
    if (batchNum) hiddenInvoiceNumbers.add(batchNum)
  }

  const filteredBase = base.filter((entry) => {
    const docId = String(entry.documentId ?? entry.id ?? '')
    if (docId && hiddenDocIds.has(docId)) return false
    const entryNum = normalizeInvoiceNumber(entry.number)
    if (
      entryNum &&
      hiddenInvoiceNumbers.has(entryNum) &&
      !purchaseHistoryEntryIncludesSeatPurchase(entry, batchList)
    ) {
      return false
    }
    return true
  })

  const extra = []

  for (let i = 0; i < batchList.length; i++) {
    const batch = batchList[i]
    if (isInitialSeatPoolBatch(batch)) {
      extra.push(mapSeatBatchToPurchaseHistoryEntry(batch, i))
      continue
    }
    const alreadyListed = filteredBase.some((entry) =>
      Boolean(findSeatBatchForPurchaseHistoryEntry(entry, [batch]))
    )
    if (!alreadyListed) {
      extra.push(mapSeatBatchToPurchaseHistoryEntry(batch, i))
    }
  }

  return [...filteredBase, ...extra].sort((a, b) => {
    const ta = new Date(a.purchaseDate).getTime()
    const tb = new Date(b.purchaseDate).getTime()
    return tb - ta
  })
}

/** @param {Record<string, unknown>} entry @param {Array<Record<string, unknown>>} batches */
export function resolvePurchaseHistoryBatch(entry, batches) {
  if (entry?.seatBatch && typeof entry.seatBatch === 'object') {
    return entry.seatBatch
  }
  return findSeatBatchForPurchaseHistoryEntry(entry, batches)
}

/** @param {Record<string, unknown>} entry */
export function purchaseHistoryHasSeatRelatedLines(entry) {
  return Boolean(entry?.seats) || Boolean(entry?.hasConversionLine)
}

/** @param {Record<string, unknown>} entry */
export function isPurchaseHistoryEntryPaid(entry) {
  const status = String(entry?.status || entry?.billingDocument?.status || '').toLowerCase()
  if (status === 'overdue' || status === 'po_received') return false
  if (status === 'paid') return true
  if (entry?.documentType === 'payment' || entry?.type === 'Payment') return true
  const fulfillment = String(
    entry?.fulfillmentStatus || entry?.billingDocument?.fulfillmentStatus || ''
  ).toLowerCase()
  return fulfillment === 'fulfilled'
}

/**
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown>[] | null | undefined} batches
 */
export function isPurchaseHistoryInvoicePaid(entry, batches = []) {
  if (isPurchaseHistoryPoReceived(entry)) return false
  const status = String(entry?.status || entry?.billingDocument?.status || '').toLowerCase()
  if (status === 'overdue') return false
  const batch = resolvePurchaseHistoryBatch(entry, batches)
  if (batch || entry?.isSeatBatchRow || entry?.documentType === 'seat_batch') {
    return true
  }
  if (entry?.type === 'Payment' || entry?.documentType === 'payment') {
    return true
  }
  return isPurchaseHistoryEntryPaid(entry)
}

/** @param {Record<string, unknown>} entry */
export function isPurchaseHistoryPoReceived(entry) {
  return (
    String(entry?.status || '').toLowerCase() === 'po_received' ||
    String(entry?.billingDocument?.status || '').toLowerCase() === 'po_received'
  )
}

/**
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown>[] | null | undefined} batches
 * @param {'all' | 'paid' | 'unpaid' | 'po_received'} filterType
 */
export function matchesPurchaseHistoryInvoicePaymentFilter(entry, batches, filterType) {
  if (filterType === 'all') return true
  if (filterType === 'po_received') {
    return isPurchaseHistoryPoReceived(entry)
  }
  const paid = isPurchaseHistoryInvoicePaid(entry, batches)
  if (filterType === 'paid') return paid
  if (filterType === 'unpaid') return !paid
  return true
}

/** @param {Record<string, unknown> | null} batch @param {Date} [now] */
export function isPurchaseHistorySeatBatchExpired(batch, now = new Date()) {
  if (!batch) return false
  if (String(batch.status || '').toLowerCase() === 'expired') return true
  const expDate = resolveBatchExpiryDate(batch)
  if (expDate && expDate <= now) return true
  return false
}

/**
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null} batch
 * @param {{ status?: string | null, daysLeft?: number | null }} [seatDisplay]
 */
export function isPurchaseHistoryRowExpired(entry, batch, seatDisplay, now = new Date()) {
  if (String(entry?.status || '').toLowerCase() === 'expired') return true
  if (seatDisplay?.status === 'Expired') return true
  if (seatDisplay?.daysLeft != null && seatDisplay.daysLeft <= 0 && batch) return true
  return isPurchaseHistorySeatBatchExpired(batch, now)
}

/**
 * Payment / fulfillment badge for purchase-history rows.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null} batch
 */
export function resolvePurchaseHistoryLicensingBadge(
  entry,
  batch,
  seatDisplay,
  now = new Date(),
  extensionRequests = []
) {
  void seatDisplay
  void now
  return resolvePurchaseHistoryPaidStatusBadge(
    entry,
    batch,
    isPurchaseHistoryEntryPaid(entry),
    extensionRequests
  )
}

/**
 * Seat columns for a purchase-history row — uses linked batch when present.
 * @param {Record<string, unknown>} entry
 * @param {Record<string, unknown> | null} batch
 */
export function getPurchaseHistorySeatDisplay(entry, batch, now = new Date()) {
  if (batch) {
    const batchExpired = isPurchaseHistorySeatBatchExpired(batch, now)
    const { quantity, assigned, available } = getBatchSeatBreakdown(batch, now)
    const expDate = resolveBatchExpiryDate(batch)
    const daysLeft = batchExpired ? 0 : computeSeatDaysLeft(expDate, now)
    const license =
      batch.type ?? batch.licenseType ?? entry.license ?? '—'
    return {
      hasSeatLine: true,
      seatsUsable: !batchExpired,
      seatsRemaining: available,
      assignedSeats: assigned,
      totalSeats: quantity,
      totalSeatsSuffix: null,
      license,
      expiryFormatted: batchExpired ? 'Expired' : formatSeatExpiryDate(expDate),
      daysLeft,
      status: batchExpired ? 'Expired' : seatExpiryStatus(daysLeft),
      expDate,
      isExpired: batchExpired,
    }
  }

  const hasSeatPurchaseLine = Boolean(entry.seats)
  const hasConversionOnly =
    Boolean(entry.hasConversionLine) && !hasSeatPurchaseLine

  if (!hasSeatPurchaseLine && !hasConversionOnly) {
    return {
      hasSeatLine: false,
      seatsUsable: false,
      seatsRemaining: null,
      assignedSeats: 0,
      totalSeats: null,
      totalSeatsSuffix: null,
      license: entry.license || '—',
      expiryFormatted: '—',
      daysLeft: null,
      status: null,
      expDate: null,
      isExpired: false,
    }
  }

  if (hasConversionOnly) {
    return {
      hasSeatLine: false,
      seatsUsable: false,
      seatsRemaining: null,
      assignedSeats: 0,
      totalSeats: null,
      totalSeatsSuffix: null,
      license: entry.license || '—',
      expiryFormatted: '—',
      daysLeft: null,
      status: null,
      expDate: null,
      isExpired: false,
    }
  }

  const paid = isPurchaseHistoryEntryPaid(entry)
  const isQuote =
    entry.documentType === 'quote' || entry.type === 'Estimate/Quote'

  return {
    hasSeatLine: true,
    seatsUsable: false,
    seatsRemaining: null,
    assignedSeats: 0,
    totalSeats: Number(entry.seats) || 0,
    totalSeatsSuffix: isQuote ? 'quoted' : paid ? 'pending' : 'ordered',
    license: entry.license || '—',
    expiryFormatted: '—',
    daysLeft: null,
    status: null,
    expDate: null,
    isExpired: false,
  }
}

/**
 * Remaining seats keyed by purchase-history entry id (batch-linked rows only).
 * @param {Array<Record<string, unknown>>} purchaseHistory
 * @param {Array<Record<string, unknown>>} batches
 */
export function buildSeatsRemainingByPurchaseHistoryId(purchaseHistory, batches) {
  const map = {}
  for (const entry of purchaseHistory || []) {
    const batch = findSeatBatchForPurchaseHistoryEntry(entry, batches)
    if (batch) {
      map[entry.id] = getBatchAvailable(batch)
    }
  }
  return map
}

/**
 * Soonest expiration for tier card + alerts — seatPurchaseBatches only.
 * @param {{
 *   seatPurchaseBatches: unknown[],
 *   seatLicenseType: string,
 * }} input
 */
export function computeSoonestSeatExpiryDisplay({
  seatPurchaseBatches,
  seatLicenseType,
}) {
  if (seatLicenseType === 'Premium' || seatLicenseType === 'Supp. Premium') {
    return { isPremium: true, formatted: null, daysLeft: null, seatsRemaining: null }
  }
  const soonest = pickSoonestSeatExpiry(
    buildSeatInsightEntriesFromBatches(seatPurchaseBatches)
  )
  if (!soonest) {
    return { isPremium: false, formatted: null, daysLeft: null, seatsRemaining: null }
  }
  return {
    isPremium: false,
    formatted: soonest.expiryFormatted,
    daysLeft: soonest.daysLeft,
    seatsRemaining: soonest.remaining,
  }
}

/**
 * Rows for seat-expiry tooling (tier modal / batch list) from seatPurchaseBatches.
 * @param {Array<Record<string, unknown>>} batches
 * @param {string} orgSeatLicenseType
 */
export function buildExpiringSeatBatchRows(batches, orgSeatLicenseType, now = new Date()) {
  return (Array.isArray(batches) ? batches : [])
    .filter((b) => getBatchAvailable(b, now) > 0)
    .map((b, idx) => {
      const available = getBatchAvailable(b, now)
      const purchase = parseSeatPurchaseDate(b.purchaseDate)
      const batchType = resolveBatchSeatLicenseType(b) ?? orgSeatLicenseType
      const expiry = resolveBatchExpiryDate(b)
      let rule
      let conditional = false
      let conditionalExpiry = null

      if (batchType === 'Premium' || batchType === 'Supp. Premium') {
        if (
          b.type === 'Basic' ||
          b.type === 'Legacy' ||
          b.type === 'Supp. Basic' ||
          b.type === 'Supp. Legacy'
        ) {
          conditional = true
          conditionalExpiry = computeSeatBatchExpiryDate(b)
          rule =
            'Upgraded from Basic — if downgraded, expires based on original purchase date'
        } else {
          rule = 'Never — Premium seats do not expire'
        }
      } else if (isLegacySeatLicenseType(batchType)) {
        rule = 'Legacy license — all seats expire Aug 1, 2028'
      } else {
        rule = `${batchType} license — 1 year from purchase date (or batch expiresAt when set)`
      }

      const daysLeft = computeSeatDaysLeft(expiry, now)
      const status = seatExpiryStatus(daysLeft)

      return {
        ...b,
        quantity: available,
        expiryDate: expiry,
        daysLeft,
        status,
        rule,
        conditional,
        conditionalExpiry,
        invoiceNum:
          b.invoiceNum || `#INV-${purchase.getFullYear()}-${String(1000 + idx)}`,
        purchaseDateFormatted: purchase.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }),
        expiryFormatted: expiry
          ? expiry.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : conditional
            ? 'Never w/Conditions'
            : 'Never',
      }
    })
    .sort((a, b) => {
      if (!a.expiryDate && !b.expiryDate) return 0
      if (!a.expiryDate) return 1
      if (!b.expiryDate) return -1
      return a.expiryDate - b.expiryDate
    })
}
